import { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

export default function Meseros() {
  const [meseros, setMeseros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null); // null = crear, objeto = editar
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    cargarMeseros();
  }, []);

  const cargarMeseros = async () => {
    try {
      // Traer todos (activos e inactivos) — ajusta el endpoint si tienes uno que traiga todos
      const res = await api.get('/meseros');
      setMeseros(res.data);
    } catch {
      Swal.fire('Error', 'No se pudieron cargar los meseros', 'error');
    } finally {
      setLoading(false);
    }
  };

  const abrirCrear = () => {
    setEditando(null);
    setNombre('');
    setShowModal(true);
  };

  const abrirEditar = (mesero) => {
    setEditando(mesero);
    setNombre(mesero.nombre);
    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setNombre('');
    setEditando(null);
  };

  const handleGuardar = async () => {
    if (!nombre.trim()) {
      Swal.fire('Error', 'El nombre es requerido', 'error');
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/meseros/${editando.id}`, { nombre: nombre.trim(), is_active: editando.is_active });
        Swal.fire('¡Listo!', 'Mesero actualizado correctamente', 'success');
      } else {
        await api.post('/meseros', { nombre: nombre.trim() });
        Swal.fire('¡Listo!', 'Mesero agregado correctamente', 'success');
      }
      cerrarModal();
      cargarMeseros();
    } catch {
      Swal.fire('Error', 'No se pudo guardar el mesero', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const toggleActivo = async (mesero) => {
    const accion = mesero.is_active ? 'desactivar' : 'activar';
    const result = await Swal.fire({
      title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} a ${mesero.nombre}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: mesero.is_active ? '#dc3545' : '#28a745',
    });
    if (!result.isConfirmed) return;
    try {
      await api.put(`/meseros/${mesero.id}`, { nombre: mesero.nombre, is_active: !mesero.is_active });
      cargarMeseros();
    } catch {
      Swal.fire('Error', 'No se pudo actualizar el mesero', 'error');
    }
  };

  const handleEliminar = async (mesero) => {
    const result = await Swal.fire({
      title: `¿Eliminar a ${mesero.nombre}?`,
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/meseros/${mesero.id}`);
      Swal.fire('¡Listo!', 'Mesero eliminado', 'success');
      cargarMeseros();
    } catch {
      Swal.fire('Error', 'No se pudo eliminar el mesero', 'error');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const activos = meseros.filter(m => m.is_active);
  const inactivos = meseros.filter(m => !m.is_active);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Meseros</h2>
          <small className="text-muted">{activos.length} activo{activos.length !== 1 ? 's' : ''}</small>
        </div>
        <button className="btn btn-primary" onClick={abrirCrear}>
          + Agregar Mesero
        </button>
      </div>

      {/* Lista activos */}
      {activos.length === 0 && inactivos.length === 0 ? (
        <div className="text-center text-muted mt-5">
          <h5>No hay meseros registrados</h5>
          <p>Agrega el primer mesero para comenzar</p>
          <button className="btn btn-primary" onClick={abrirCrear}>+ Agregar Mesero</button>
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            {activos.map((m) => (
              <div key={m.id} className="col-12 col-md-6 col-lg-4">
                <div className="card h-100" style={{ borderLeft: '4px solid #28a745', borderRadius: '10px' }}>
                  <div className="card-body d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      {/* Avatar */}
                      <div style={{
                        width: 42, height: 42, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 700, fontSize: 16, flexShrink: 0,
                      }}>
                        {m.nombre.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="fw-semibold">{m.nombre}</div>
                        <span className="badge bg-success" style={{ fontSize: '11px' }}>Activo</span>
                      </div>
                    </div>
                    {/* Acciones */}
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" title="Editar" onClick={() => abrirEditar(m)}>
                        ✏️
                      </button>
                      <button className="btn btn-sm btn-outline-warning" title="Desactivar" onClick={() => toggleActivo(m)}>
                        🔕
                      </button>
                      <button className="btn btn-sm btn-outline-danger" title="Eliminar" onClick={() => handleEliminar(m)}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Inactivos */}
          {inactivos.length > 0 && (
            <>
              <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                <strong>Inactivos ({inactivos.length})</strong>
              </p>
              <div className="row g-3">
                {inactivos.map((m) => (
                  <div key={m.id} className="col-12 col-md-6 col-lg-4">
                    <div className="card h-100" style={{ borderLeft: '4px solid #6c757d', borderRadius: '10px', opacity: 0.65 }}>
                      <div className="card-body d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                          <div style={{
                            width: 42, height: 42, borderRadius: '50%',
                            background: '#6c757d',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 700, fontSize: 16, flexShrink: 0,
                          }}>
                            {m.nombre.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-semibold text-muted">{m.nombre}</div>
                            <span className="badge bg-secondary" style={{ fontSize: '11px' }}>Inactivo</span>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-success" title="Activar" onClick={() => toggleActivo(m)}>
                            ✅
                          </button>
                          <button className="btn btn-sm btn-outline-danger" title="Eliminar" onClick={() => handleEliminar(m)}>
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Modal Crear / Editar */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editando ? 'Editar Mesero' : 'Agregar Mesero'}</h5>
                <button className="btn-close" onClick={cerrarModal} />
              </div>
              <div className="modal-body">
                <label className="form-label">Nombre completo *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Carlos Martínez"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleGuardar()}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={cerrarModal}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleGuardar} disabled={guardando}>
                  {guardando ? 'Guardando...' : editando ? 'Guardar Cambios' : 'Agregar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}